import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { useDescriptionSuggestions } from '@/hooks/use-entries';
import { cn } from '@/lib/cn';
import { useDebouncedValue } from '@/lib/use-debounced-value';

const DEFAULT_DEBOUNCE_MS = 250;

interface DescriptionAutocompleteProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  maxLength?: number;
  placeholder?: string;
  debounceMs?: number;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/**
 * Free-text description with suggestions from the caller's own history (architecture sprint-03
 * §9.5). The field value is always the typed text; a suggestion only replaces it when chosen.
 * Enter with a highlighted suggestion selects it without submitting the form.
 */
export function DescriptionAutocomplete({
  id,
  value,
  onChange,
  onBlur,
  maxLength,
  placeholder,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: DescriptionAutocompleteProps): JSX.Element {
  const [focused, setFocused] = useState(false);
  // Closed by Escape or a selection until the user types again.
  const [dismissed, setDismissed] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const debounced = useDebouncedValue(value.trim(), debounceMs);
  const suggestions = useDescriptionSuggestions(debounced, focused);
  const items = suggestions.data ?? [];
  const open = focused && !dismissed && items.length > 0;

  function choose(item: string | undefined): void {
    if (item === undefined) return;
    onChange(item);
    setDismissed(true);
    setHighlight(-1);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (items.length === 0) return;
      event.preventDefault();
      if (!open) {
        setDismissed(false);
        setHighlight(event.key === 'ArrowDown' ? 0 : items.length - 1);
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => (h + step + items.length) % items.length);
      return;
    }
    if (event.key === 'Enter' && open && highlight >= 0) {
      // Selecting must not submit the surrounding form.
      event.preventDefault();
      choose(items[highlight]);
    }
  }

  const activeId = open && highlight >= 0 ? `${listId}-${highlight}` : undefined;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setDismissed(true);
          setHighlight(-1);
        }
      }}
    >
      <PopoverAnchor asChild>
        <Input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          autoComplete="off"
          maxLength={maxLength}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setDismissed(false);
            setHighlight(-1);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setHighlight(-1);
            onBlur?.();
          }}
          onKeyDown={onKeyDown}
        />
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popper-anchor-width)] p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Clicking back into the input is not "outside".
          if (e.target instanceof Node && inputRef.current?.contains(e.target)) e.preventDefault();
        }}
      >
        <ul id={listId} role="listbox" aria-label="Description suggestions">
          {items.map((item, index) => (
            <li
              key={item}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === highlight}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(item)}
              onMouseMove={() => setHighlight(index)}
              className={cn('block cursor-default select-none truncate rounded px-2 py-1.5 text-sm text-muted-foreground', index === highlight && 'bg-accent text-foreground')}
            >
              {item}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
